/**
 * A2UISurface — renders an A2UI surface (flat component adjacency list +
 * data model) using the Papan catalog. See types.ts for the subset scope.
 *
 * Fail-soft by construction (payloads are untrusted LLM output):
 *   • unknown component name → placeholder chip, siblings still render
 *   • missing child reference → placeholder chip
 *   • reference cycles / runaway depth → cut off at the repeated id
 * Nothing in a payload can throw past this component, and nothing ever
 * renders as HTML — strings go through React text nodes.
 *
 * Data flow: bindings (`{path}` props) read from a local data-model
 * state seeded by `surface.dataModel`; input components write back via
 * setData. Firing an action surfaces `{name, context, data}` through
 * `onEvent` — the caller decides the transport (question reply pipe for
 * interactive prompts; composer prefill for display surfaces).
 */
import { useMemo, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import { A2UI_CATALOG, type A2UIRenderCtx } from './catalog.js'
import { getPath, setPath, isBinding, type A2UIAction, type A2UIComponent, type A2UIEvent, type A2UISurfaceDef } from './types.js'

interface Props {
  surface: A2UISurfaceDef
  /** User fired an event (Button). Omit to render a frozen surface. */
  onEvent?: (ev: A2UIEvent) => void
  /** Force-freeze (answered prompts, historical scrollback). */
  disabled?: boolean
  /** Open a workspace file (FileCard clicks). */
  onOpenFile?: (path: string) => void
}

const MAX_DEPTH = 64

function Placeholder({ children }: { children: ReactNode }) {
  // self-start + w-fit: flex-column parents stretch children by default,
  // which turned these chips into ugly full-width dashed bars.
  return (
    <span className="inline-block self-start w-fit text-[10px] font-mono themed-text-muted opacity-80 border border-dashed themed-border-secondary rounded px-1.5 py-0.5">
      {children}
    </span>
  )
}

export function A2UISurface({ surface, onEvent, disabled, onOpenFile }: Props) {
  const byId = useMemo(() => {
    const m = new Map<string, A2UIComponent>()
    for (const c of surface.components) if (!m.has(c.id)) m.set(c.id, c)
    return m
  }, [surface])

  const [dataModel, setDataModel] = useState<Record<string, unknown>>(
    () => ({ ...(surface.dataModel ?? {}) }),
  )

  const frozen = !!disabled || !onEvent

  const fire = useCallback((action: A2UIAction | undefined) => {
    const name = action?.event?.name
    if (!name || frozen || !onEvent) return
    // Read the CURRENT model via the setter to dodge stale closures from
    // catalog components memoized against an older ctx.
    setDataModel(current => {
      onEvent({ name, context: action.event.context, data: current })
      return current
    })
  }, [frozen, onEvent])

  const setData = useCallback((path: string, value: unknown) => {
    setDataModel(current => setPath(current, path, value))
  }, [])

  // Depth-first render over the adjacency list. `trail` carries the ids on
  // the CURRENT branch — a repeat means a reference cycle, not reuse (the
  // same id may legitimately appear under two different parents).
  const renderNode = (id: string, trail: string[]): ReactNode => {
    if (trail.includes(id) || trail.length > MAX_DEPTH) {
      return <Placeholder key={`${trail.join('.')}:${id}`}>cycle: {id}</Placeholder>
    }
    const comp = byId.get(id)
    if (!comp) return <Placeholder key={`${trail.join('.')}:${id}`}>missing: {id}</Placeholder>
    const renderer = A2UI_CATALOG[comp.component]
    if (!renderer) return <Placeholder key={`${trail.join('.')}:${id}`}>unsupported: {comp.component}</Placeholder>

    const nextTrail = [...trail, id]
    const ctx: A2UIRenderCtx = {
      resolve: (v) => (isBinding(v) ? getPath(dataModel, v.path) : v),
      bindPath: (v) => (isBinding(v) ? v.path : null),
      setData,
      fire,
      renderChildren: (c) => {
        const ids = [...(c.child ? [c.child] : []), ...(c.children ?? [])]
        if (ids.length === 0) return null
        return ids.map(childId => (
          <FragmentKeyed key={childId}>{renderNode(childId, nextTrail)}</FragmentKeyed>
        ))
      },
      disabled: frozen,
      onOpenFile,
    }
    return renderer(comp, ctx)
  }

  return (
    <div className="a2ui-surface space-y-2 text-sm" data-a2ui-surface={surface.surfaceId || true}>
      {renderNode(surface.root, [])}
    </div>
  )
}

/** Keyed wrapper — catalog renderers return arbitrary nodes that may not
 *  accept a key, so the key lives on this wrapper element instead. */
function FragmentKeyed({ children }: { children: ReactNode }) {
  return <>{children}</>
}
